import { forwardRef, isValidElement } from 'react';
import IconButton from './IconButton';
import { Menu } from '@headlessui/react';
import { cn } from '@/ui/design-system/utils/cn';

export const MenuWrapper = forwardRef(function MenuWrapper(
    { className, ...props },
    ref,
) {
    return (
        <div
            ref={ref}
            className={cn('inline-flex relative', className)}
            {...props}
        />
    );
});

export const MenuItems = forwardRef(function MenuItems(
    { $menuAlign = 'right', $space, $wrap, className, children, ...props },
    ref,
) {
    return (
        <div
            ref={ref}
            className={cn(
                'absolute top-[calc(100%+2px)] z-50 w-[13.75rem] max-h-[22.5rem] overflow-y-auto',
                'bg-background rounded-md shadow-md',
                'flex flex-col items-stretch',
                $menuAlign === 'left' ? 'left-0' : 'right-0',
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
});

export const MenuSeparator = forwardRef(function MenuSeparator(
    { className, ...props },
    ref,
) {
    return (
        <div
            ref={ref}
            className={cn('min-h-px bg-border', className)}
            {...props}
        />
    );
});

export const MenuItem = forwardRef(function MenuItem(
    { wrapText = true, active, selected, className, ...props },
    ref,
) {
    return (
        <IconButton
            ref={ref}
            variant="ghost"
            wrapText={wrapText}
            className={cn(
                'border-0 basis-9 justify-start px-3 py-2 rounded-none w-full text-foreground',
                (active || selected) && 'bg-accent',
                className,
            )}
            {...props}
        />
    );
});

const defaultButtonProps = {
    as: IconButton,
    children: 'Menu',
};

function MenuButton({
    buttonProps,
    options,
    menuAlign = 'left',
    mixpanelMeta,
    mixpanelContext = 'MenuButton',
    MenuWrapper: MenuWrapperComponent = MenuWrapper,
    MenuItems: MenuItemsComponent = MenuItems,
    MenuItem: MenuItemComponent = MenuItem,
    ...props
}) {
    return (
        <Menu as={MenuWrapperComponent} {...props}>
            {({ open }) => (
                <>
                    <Menu.Button
                        {...{
                            mixpanelMeta,
                            mixpanelContext,
                        }}
                        {...defaultButtonProps}
                        {...buttonProps}
                        $isOpen={open}
                    />
                    <Menu.Items as={MenuItemsComponent} $menuAlign={menuAlign}>
                        {options.map((option, idx) => {
                            if (isValidElement(option)) return option;
                            const {
                                key,
                                mixpanelMeta: optionMixpanelMeta,
                                ...restOption
                            } = option;
                            return (
                                <Menu.Item key={key || idx}>
                                    {({ active, disabled }) => (
                                        <MenuItemComponent
                                            disabled={disabled}
                                            className={active ? 'active' : ''}
                                            mixpanelMeta={{
                                                ...mixpanelMeta,
                                                ...optionMixpanelMeta,
                                            }}
                                            mixpanelContext={mixpanelContext}
                                            {...restOption}
                                        />
                                    )}
                                </Menu.Item>
                            );
                        })}
                    </Menu.Items>
                </>
            )}
        </Menu>
    );
}

export default MenuButton;
